import { useCallback, useState, useMemo } from "react";
import { DomainCompetency } from "Stores/courseStore";
import { EAIClient } from "Services/EAIClient";
import { Utils } from "../common/utils";
import { useAppContext } from "@/contexts/app";
import useSWR from "swr";
import * as Sentry from "@sentry/react";

export interface StandardCompetencyResponse {
    result: Result;
    data: DomainCompetency[];
}

interface Result {
    code: number;
    message: string;
}

const clientConfigs = Utils.getEAIClientConfigs();
const client = new EAIClient();

export function useStandardCompetencies() {
    const [openUploadModal, setOpenUploadModal] = useState(false);
    const [openDomainStandardCompetenciesModal, setOpenDomainStandardCompetenciesModal] = useState(false);
    const [schoolEnabledOnly, setSchoolEnabledOnly] = useState(false);
    const { dispatchError } = useAppContext();

    const apiEndpoint = useMemo(() =>
        `${clientConfigs.endpoint}/schools/${clientConfigs.school}/curriculum_mapping/standard_domain_competency?school_enabled_only=${schoolEnabledOnly}`,
        [schoolEnabledOnly]
    );

    const { data, error, isLoading, mutate } = useSWR<StandardCompetencyResponse>(apiEndpoint);

    const items = useMemo(() => data?.data || [], [data]);

    const findItem = useCallback((items: DomainCompetency[], ruuid: string): DomainCompetency | undefined => {
        for (const item of items) {
            if (item.ruuid === ruuid) {
                return item;
            }
            const found = findItem(item.children, ruuid);
            if (found) return found;
        }
        return undefined;
    }, []);

    const toggleSchoolEnabled = useCallback(
        async (ruuid: string) => {
            const itemToUpdate = findItem(items, ruuid);
            if (!itemToUpdate) return;

            const updatedItem = {
                ...itemToUpdate,
                school_enabled: !itemToUpdate.school_enabled
            };

            try {
                await client.updateStandardCompetency(updatedItem, ruuid);
                await mutate();
            } catch (error) {
                Sentry.captureException(error);
                dispatchError(error);
                await mutate();
            }
        },
        [mutate, items, dispatchError, findItem]
    );

    return {
        data,
        items,
        openUploadModal,
        setOpenUploadModal,
        toggleSchoolEnabled,
        isLoading,
        error,
        openDomainStandardCompetenciesModal,
        setOpenDomainStandardCompetenciesModal,
        schoolEnabledOnly,
        setSchoolEnabledOnly,
        mutate
    };
}